import type {
  MentionColorVariant,
  MentionOptionDetail,
} from '../ui/tiptap/extensions';

// ─── Categories & Tabs ──────────────────────────────────────────────

export type ResourcePickerCategory =
  | 'agent'
  | 'document'
  | 'folder'
  | 'trigger'
  | 'dataset'
  | 'tool'
  | 'workflow'
  | 'input'
  /** Builtin graph steps (for-each, filter, emit-event) — not a stored resource. */
  | 'flow-control';

export type ResourcePickerTab =
  | 'all'
  | 'tools'
  | 'docs'
  | 'datasets'
  | 'agents'
  | 'workflows'
  | 'folders'
  | 'flow';

/** Maps a tab to the categories it displays. `'all'` means every category. */
export const TAB_CATEGORIES: Record<
  ResourcePickerTab,
  ResourcePickerCategory[] | 'all'
> = {
  all: 'all',
  tools: ['tool'],
  docs: ['document'],
  datasets: ['dataset'],
  agents: ['agent'],
  workflows: ['workflow'],
  folders: ['folder'],
  flow: ['flow-control'],
};

export const TAB_LABELS: Record<ResourcePickerTab, string> = {
  all: 'All',
  tools: 'Tools',
  docs: 'Docs',
  datasets: 'Datasets',
  agents: 'Agents',
  workflows: 'Workflows',
  folders: 'Folders',
  flow: 'Flow control',
};

export const ALL_TABS: readonly ResourcePickerTab[] = [
  'all',
  'tools',
  'docs',
  'datasets',
  'agents',
  'workflows',
  'folders',
  'flow',
] as const;

// ─── Item ───────────────────────────────────────────────────────────

export type ResourcePickerItem<T = unknown> = {
  /** Unique id (referenceId, tool key, resource id, etc.) */
  id: string;
  label: string;
  category: ResourcePickerCategory;
  /** Provider grouping key for tools (e.g. 'github'). */
  provider?: string;
  /** Human-readable provider label (e.g. 'GitHub'). */
  providerLabel?: string;
  /** The data payload emitted on selection. */
  data: T;
  /** Color dot variant. */
  color?: MentionColorVariant;
  /** Whether the tool is connected / ready to use. */
  available?: boolean;
  /** Rich detail for tool preview panel. */
  detail?: MentionOptionDetail;
  /** Type label shown after the name (e.g. 'Tool', 'Agent'). */
  typeLabel?: string;
  /** Sub-label badge (e.g. '3 tools', '5 steps', '12 items'). */
  badge?: string;
  /** Folder-specific: number of children items. */
  childrenCount?: number;
  /** Extra search keywords (provider name, type label, etc.). */
  searchKeywords?: string[];
  /** Icon node (e.g. provider logo, category icon). */
  icon?: React.ReactNode;
};

// ─── Visible rows (computed from items + state) ─────────────────────

export type VisibleRow<T = unknown> =
  | {
      kind: 'group-header';
      /** Absent for synthetic sections such as Recent. */
      category?: ResourcePickerCategory;
      label: string;
      icon?: React.ReactNode;
    }
  | {
      /**
       * Leaves the picker for the full catalogue. Sits last, after every
       * result, because it is what you reach for when nothing here fits.
       */
      kind: 'browse-all';
      label: string;
      navIndex: number;
    }
  | {
      /**
       * Expands a capped section in place. Navigable, so the whole list stays
       * reachable from the keyboard without touching the mouse.
       */
      kind: 'show-more';
      category: ResourcePickerCategory;
      label: string;
      navIndex: number;
    }
  | {
      kind: 'item';
      item: ResourcePickerItem<T>;
      indented?: boolean;
      /**
       * Show the type beside the name. Only for rows whose group heading does
       * not already say it: under an AGENTS heading a trailing "Agent" on every
       * row is noise, and it steals the width the name needs.
       */
      showTypeLabel?: boolean;
      /** Index into the flat navigable list (rows that can be focused). */
      navIndex: number;
    }
  | {
      kind: 'provider-trigger';
      provider: string;
      label: string;
      icon?: React.ReactNode;
      toolCount: number;
      expanded: boolean;
      /** True if any tool in this provider is available. */
      someAvailable: boolean;
      /** Index into the flat navigable list. */
      navIndex: number;
    };

// ─── Mode & Callbacks ───────────────────────────────────────────────

export type ResourcePickerMode = 'single' | 'multi';

export type ResourcePickerCallbacks<T = unknown> = {
  onSelect?: (item: ResourcePickerItem<T>) => void;
  onMultiConfirm?: (items: ResourcePickerItem<T>[]) => void;
  onDismiss?: () => void;
};
